import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { HourglassTopOutlined, LockPersonOutlined, VerifiedUserOutlined } from "@mui/icons-material";
import { DASH } from "../../dashboard/tokens";
import type { PermitSummary } from "../../../../Services/permitSummaryApi";
import { describePermits } from "../../dashboard/PermitTeaser";

type Props = {
  pending?: boolean;
  /** Someone else has verified this home: say so, and offer the dispute route (Oct 20 call). */
  claimed?: boolean;
  onClaim?: () => void;
  variant?: "glass" | "solid";
  /** Public permits on this parcel: the reason to claim (October 2026). */
  permitSummary?: PermitSummary | null;
};

/**
 * Right side of the header for a visitor: invite the owner to claim, confirm a
 * claim is in review, or say the home is already claimed and how to dispute it.
 */
const HeaderClaimCard: React.FC<Props> = ({ pending, claimed, onClaim, variant = "glass", permitSummary }) => {
  const glass = variant === "glass";
  const fg = glass ? "#fff" : "#1d2b2b";
  const sub = glass ? "rgba(255,255,255,0.8)" : DASH.muted;
  const permits = permitSummary && !claimed ? describePermits(permitSummary) : null;
  return (
    <Box
      sx={{
        borderRadius: "16px",
        p: { xs: 4, md: 5 },
        width: "100%",
        maxWidth: { md: 320 },
        color: fg,
        ...(glass
          ? { bgcolor: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.28)", backdropFilter: "blur(10px)" }
          : { bgcolor: DASH.cream, border: `1px solid ${DASH.border}` }),
      }}
      data-testid="header-claim-card"
    >
      <Stack direction="row" spacing={3} alignItems="flex-start">
        <Box
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: glass ? "rgba(255,255,255,0.18)" : DASH.tealTint2,
            color: glass ? "#fff" : DASH.teal,
          }}
        >
          {pending ? <HourglassTopOutlined /> : claimed ? <LockPersonOutlined /> : <VerifiedUserOutlined />}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 17 }}>
            {pending ? "We're reviewing your claim" : claimed ? "This home has been claimed" : "Is this your home?"}
          </Typography>
          {permits && (
            <Box
              sx={{
                mt: 1.5,
                px: 2,
                py: 1.25,
                borderRadius: "10px",
                bgcolor: glass ? "rgba(255,255,255,0.16)" : DASH.amberTint,
              }}
              data-testid="header-permit-teaser"
            >
              <Typography sx={{ fontWeight: 800, fontSize: 14, color: glass ? "#fff" : "#1d2b2b" }}>
                {permits.headline}
              </Typography>
              {permits.detail && (
                <Typography sx={{ fontSize: 12.5, mt: 0.25, color: glass ? "rgba(255,255,255,0.85)" : "#5b3a00" }}>
                  {permits.detail}
                </Typography>
              )}
            </Box>
          )}
          <Typography sx={{ mt: 1, fontSize: 14, color: sub, lineHeight: 1.5 }}>
            {pending
              ? permits
                ? "You'll see each permit as soon as your claim is approved. We'll email you."
                : "You'll get an email as soon as it's approved — then this dashboard is yours."
              : claimed
              ? "Its owner has verified it on SimplerHome. If it's yours, send us proof of ownership and our team will review it."
              : permits
              ? "Claim it free to see each one and add them to your home's history."
              : "Claim it free. Once we verify it, the home is locked to you and its full dashboard opens up."}
          </Typography>
          {!pending && onClaim && (
            <Button
              onClick={onClaim}
              sx={{
                mt: 3,
                bgcolor: glass ? "#fff" : DASH.teal,
                color: glass ? DASH.teal : "#fff",
                fontWeight: 700,
                borderRadius: 2,
                px: 4,
                "&:hover": { bgcolor: glass ? "#f1f5f5" : DASH.tealDark },
              }}
              data-testid="header-claim-card-btn"
            >
              {claimed ? "Send proof of ownership" : "Claim this home"}
            </Button>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default HeaderClaimCard;
